import React from 'react';
import { motion } from 'framer-motion';
import {
  Shield,
  Zap,
  Award,
  Settings,
  Truck,
  Bike,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackBrandClick } from '../../lib/analytics';
import { useFirestoreCollection } from '../../hooks/useFirestoreCollection';
import { FirebaseImage } from '../FirebaseImage';
import type { TireBrand } from '../../types/firebase';

const TireSpecialSection: React.FC = () => {
  const { t, translations } = useLanguage();

  const { data: brands } = useFirestoreCollection<TireBrand>({
    collection: 'tire-brands',
    where: [['published', '==', true]],
    orderBy: ['order', 'asc'],
  });

  const tireTypes = [
    {
      id: 'car',
      icon: Shield,
      image: '/pneu.jpg',
      title: t('home.tireSpecial.tireTypes.car.title'),
      description: t('home.tireSpecial.tireTypes.car.description'),
      features: translations.home?.tireSpecial?.tireTypes?.car?.features || [],
      color: 'from-red-600 to-red-700'
    },
    {
      id: 'truck',
      icon: Truck,
      image: '/pneu-camion.jpg',
      title: t('home.tireSpecial.tireTypes.truck.title'),
      description: t('home.tireSpecial.tireTypes.truck.description'),
      features: translations.home?.tireSpecial?.tireTypes?.truck?.features || [],
      color: 'from-gray-700 to-gray-800'
    },
    {
      id: 'moto',
      icon: Bike,
      image: '/pneu-moto.jpg',
      title: t('home.tireSpecial.tireTypes.moto.title'),
      description: t('home.tireSpecial.tireTypes.moto.description'),
      features: translations.home?.tireSpecial?.tireTypes?.moto?.features || [],
      color: 'from-red-700 to-red-800'
    },
    {
      id: 'classic',
      icon: Award,
      image: '/pneu-classique.jpg',
      title: t('home.tireSpecial.tireTypes.classic.title'),
      description: t('home.tireSpecial.tireTypes.classic.description'),
      features: translations.home?.tireSpecial?.tireTypes?.classic?.features || [],
      color: 'from-black to-gray-900'
    }
  ];

  const advantages = translations.home?.tireSpecial?.advantages?.items || [];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white overflow-hidden relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #E30613 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, #E30613 0%, transparent 50%)`
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ 
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="inline-block mb-6"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-red-600 to-red-500 rounded-full flex items-center justify-center shadow-2xl">
              <Shield className="w-10 h-10 text-white" />
            </div>
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600">
              {t('home.tireSpecial.title')}
            </span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t('home.tireSpecial.subtitle')}
          </p>
          
          <div className="w-32 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto mt-8 rounded-full" />
        </motion.div>

        {/* Tire Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {tireTypes.map((tire, index) => {
            const IconComponent = tire.icon;
            
            return (
              <motion.div
                key={tire.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
              >
                {/* Card */}
                <motion.div
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden h-full shadow-2xl hover:shadow-red-500/20 transition-all duration-300 border border-gray-700 hover:border-red-500/50"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <motion.img
                      src={tire.image}
                      alt={tire.title}
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
                    
                    {/* Icon Overlay */}
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className={`absolute top-4 right-4 w-12 h-12 rounded-xl bg-gradient-to-r ${tire.color} flex items-center justify-center shadow-lg`}
                    >
                      <IconComponent className="w-6 h-6 text-white" />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 group-hover:text-red-400 transition-colors">
                      {tire.title}
                    </h3>
                    
                    <p className="text-gray-400 mb-4 leading-relaxed">
                      {tire.description}
                    </p>

                    {/* Features */}
                    <ul className="space-y-2">
                      {tire.features.map((feature, featureIndex) => (
                        <motion.li
                          key={featureIndex}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 + featureIndex * 0.1 }}
                          className="flex items-center space-x-2 text-sm text-gray-300"
                        >
                          <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>

                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/5 to-red-600/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </motion.div>
            );
          })}
        </div>

        {/* Advantages Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-gradient-to-r from-red-600/10 to-red-700/10 rounded-3xl p-12 backdrop-blur-sm border border-red-600/20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">{t('home.tireSpecial.advantages.title')}</h3>
            <p className="text-gray-300 text-lg">{t('home.tireSpecial.advantages.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {advantages.map((advantage, index) => {
              const iconComponents = [Zap, Settings, Shield];
              const IconComponent = iconComponents[index] || Zap;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  className="text-center group"
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 10 }}
                    className="w-16 h-16 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-red-500/50 transition-shadow duration-300"
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </motion.div>
                  
                  <h4 className="text-xl font-bold mb-2 group-hover:text-red-400 transition-colors">
                    {advantage.title}
                  </h4>
                  
                  <p className="text-gray-400">
                    {advantage.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Brand Logos Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">{t('home.brands.title')}</h3>
            <div className="w-16 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto rounded-full mb-6" />
            <p className="text-gray-300 text-lg">{t('home.brands.subtitle')}</p>
          </div>

          {/* Brand Logos Grid */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-600/10 to-red-700/10 backdrop-blur-sm border border-red-600/20 p-12">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-8 items-center justify-items-center">
              {brands.map((brand, index) => (
                <motion.div
                  key={brand.id}
                  initial={{ opacity: 0, scale: 0.5, y: 20 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.6,
                    delay: 0.1 * index,
                    type: "spring",
                    damping: 15
                  }}
                  whileHover={{
                    scale: 1.2,
                    y: -10,
                    rotateY: 10,
                    transition: { duration: 0.3 }
                  }}
                  className="relative group"
                >
                  <div className="relative p-4 bg-white rounded-2xl border border-gray-200 shadow-lg group-hover:shadow-xl transition-all duration-300 w-28 h-28">
                    <div
                      onClick={() => trackBrandClick(brand.name, 'tire_special_section')}
                      className="cursor-pointer"
                    >

                    <FirebaseImage
                      src={brand.logoUrl}
                      alt={`Logo ${brand.name}`}
                      className="relative z-10 w-full h-full object-contain transition-all duration-300"
                    />

                    </div>

                    {/* Brand Name Tooltip */}
                    <div className="absolute -bottom-14 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                      <div className="bg-black/80 text-white text-sm font-medium px-3 py-1 rounded-lg whitespace-nowrap">
                        {brand.name}
                      </div>
                      <div className="w-2 h-2 bg-black/80 transform rotate-45 mx-auto -mt-1" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Additional Brands Indicator */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 1 }}
              className="text-center mt-12"
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.05, 1],
                  opacity: [0.7, 1, 0.7]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20"
              >
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                </div>
                <span className="text-white font-medium">{t('home.brands.more')}</span>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <span>{t('home.tireSpecial.cta.primary')}</span>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowRight className="w-5 h-5" />
              </motion.div>
            </motion.a>

            <motion.a
              href="/services"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-3 px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-200"
            >
              <span>{t('home.tireSpecial.cta.secondary')}</span>
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </div>
        </motion.div>

        {/* Floating Elements */}
        <motion.div
          animate={{ 
            y: [0, -20, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 right-10 w-6 h-6 bg-red-500 rounded-full opacity-30"
        />
        
        <motion.div
          animate={{ 
            y: [0, 20, 0],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{ 
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5
          }}
          className="absolute bottom-20 left-10 w-4 h-4 bg-red-400 rounded-full opacity-40"
        />
      </div>
    </section>
  );
};

export default TireSpecialSection;